import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { 
  Header,
  Footer,
  Main,
  Container,
  SEO
} from '../../components';
import ProsjektHero from '../../components/ProsjektPost/ProsjektHero/ProsjektHero';
import ProsjektCustomerInfo from '../../components/ProsjektPost/ProsjektCustomerInfo/ProsjektCustomerInfo';
import ProsjektContent from '../../components/ProsjektPost/ProsjektContent/ProsjektContent';
import classNames from 'classnames/bind';
import styles from '../../styles/Single.module.scss';
import * as MENUS from '../../constants/menus';
import { ProsjektContentFragment } from '../../fragments/project/ProsjektContentFragment';
import { SEOFragment } from '../../fragments/SEOFragment';

const cx = classNames.bind(styles);

export async function getStaticPaths() {
  // Use fallback blocking to generate pages on-demand
  // This is more reliable for production deployment
  return {
    paths: [],
    fallback: 'blocking'
  };
}

export async function getStaticProps({ params }) {
  const { slug } = params;
  
  // Debug logging
  if (process.env.NODE_ENV === 'development' || process.env.DEBUG_ROUTING === 'true') {
    const { logEnvironmentInfo, logRequestInfo } = await import('../../lib/debug-utils');
    logEnvironmentInfo();
    logRequestInfo({ params }, 'Prosjekt SSG');
  }
  
  const { getApolloClient } = await import('@faustwp/core');
  const { executeGraphQLWithRetry } = await import('../../lib/graphql-utils');
  const apolloClient = getApolloClient();
  
  try {
    const [projectResult, sharedResult] = await Promise.all([
      // Fetch project data with retry logic
      executeGraphQLWithRetry(apolloClient, gql`
          ${ProsjektContentFragment}
          ${SEOFragment}
          query GetProsjektByUri($uri: String!) {
            nodeByUri(uri: $uri) {
              ... on Prosjekt {
                id
                title
                slug
                uri
                modified
                content
                date
                featuredImage {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
                ...ProsjektContentFragment
                ...SEOFragment
              }
            }
          }
        `, { uri: `/prosjekt/${slug}` }),
      
      // Fetch shared data with retry logic
      executeGraphQLWithRetry(apolloClient, gql`
          query GetSharedDataForProject($headerLocation: MenuLocationEnum!, $footerLocation: MenuLocationEnum!) {
            generalSettings {
              title
              description
            }
            headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
              nodes {
                id
                label
                path
                parentId
                cssClasses
                menu {
                  node {
                    name
                  }
                }
              }
            }
            footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
              nodes {
                id
                label
                path
                parentId
                cssClasses
                menu {
                  node {
                    name
                  }
                }
              }
            }
            mediaboosterSettings {
              mediaboosterSettingsFields {
                logo {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
                logoIcon {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
              }
            }
          }
        `, {
          headerLocation: MENUS.PRIMARY_LOCATION,
          footerLocation: MENUS.FOOTER_LOCATION,
        })
    ]);
    
    const project = projectResult.data?.nodeByUri;
    const sharedData = sharedResult.data;
    
    // Return 404 if project is not found
    if (!project) {
      return {
        notFound: true,
        revalidate: 60, // Retry quickly in case this 404 is from temporary data lag
      };
    }
    
    return {
      props: {
        project,
        sharedData,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error) {
    console.error('Error fetching project data:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      graphQLErrors: error.graphQLErrors,
      networkError: error.networkError
    });
    return {
      notFound: true,
      revalidate: 60, // Don't keep transient failures as long-lived cached 404
    };
  }
}

export default function Project({ project, sharedData }) {
  const router = useRouter();
  
  const { title, featuredImage, seo, oppdragPost, content, date } = project;
  const { title: siteTitle, description: siteDescription } = sharedData?.generalSettings ?? {};
  const primaryMenu = sharedData?.headerMenuItems?.nodes ?? [];
  const footerMenu = sharedData?.footerMenuItems?.nodes ?? [];
  const lightLogo = sharedData?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;

  // SEO values 
  const pageTitle = seo?.title || title || 'Prosjekt';
  const pageDescription = seo?.description || content || `${title} - Prosjekt hos Mediabooster`;
  const pageImage = seo?.openGraph?.image?.secureUrl || seo?.openGraph?.image?.url || featuredImage?.node?.mediaItemUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
  
  // Canonical URL
  const canonicalUrl = seo?.canonicalUrl || (router.asPath === '/' 
    ? (process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no') 
    : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no'}${router.asPath}`);



  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        imageUrl={pageImage}
        url={canonicalUrl}
        siteName={siteTitle}
        ogType="article"
        seo={{
          robots: seo?.robots,
          canonicalUrl,
        }}
      />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        sharedData={sharedData}
      />
      <Main style={{ paddingBottom: '8rem' }}>
        <article className={cx('article')}>
          <ProsjektHero data={project} />
          {oppdragPost && (
            <ProsjektCustomerInfo data={oppdragPost} />
          )}
          {oppdragPost?.layout && (
            <ProsjektContent layout={oppdragPost.layout} />
          )}
        </article>
      </Main>
      <Footer
        title={siteTitle}
        menuItems={footerMenu}
        sharedData={sharedData}
      />
    </>
  );
} 